import React, { useState, useEffect, useCallback } from 'react';
import {
  Select, Space, Typography, Tag, Table, Button, Card, Spin, Empty,
  Popconfirm, Tooltip, message,
} from 'antd';
import { FilterOutlined, ReloadOutlined, SyncOutlined, CheckCircleOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCropListFilterApi, getFarmListApi, getPlotsApi, getCropNamesApi, changeCropStatusApi, deleteCropApi } from '../../api/crops';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { CROP_NAME_IMAGE, AREA_UNITS } from '../../utils/constants';
import iconCrop from '../../icons/crop.png';
import HarvestModal from '../../components/HarvestModal';
import dayjs from 'dayjs';

const { Title } = Typography;

const currentYear    = dayjs().year();
const YEAR_OPTIONS   = Array.from({ length: 5 }, (_, i) => ({ value: String(currentYear - i), label: String(currentYear - i) }));
const STATUS_OPTIONS = [
  { value: '',  label: 'All Status' },
  { value: '1', label: 'Active' },
  { value: '0', label: 'Harvested' },
];
const PER_PAGE_OPTIONS = [10, 20, 50].map((v) => ({ value: v, label: `${v} / page` }));

export default function CropList() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { t, i18n } = useTranslation();
  const lang        = i18n.language || 'en';

  const [crops, setCrops]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 10 });

  const [years, setYears]     = useState(() => {
    const saved = sessionStorage.getItem('croplist_years');
    const parentYears = location.state?.years;
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parentYears) {
        const filtered = parsed.filter((y) => parentYears.includes(y));
        return filtered.length > 0 ? filtered : parentYears;
      }
      return parsed;
    }
    return parentYears || [String(currentYear)];
  });
  const [farmId, setFarmId]   = useState(undefined);
  const [plotIds, setPlotIds] = useState([]);
  const [status, setStatus]   = useState('');
  const [perPage, setPerPage] = useState(10);

  const [commonId, setCommonId] = useState(undefined);

  const [farms, setFarms]       = useState([]);
  const [plots, setPlots]       = useState([]);
  const [cropNames, setCropNames] = useState([]);

  const [harvestingCrop, setHarvestingCrop] = useState(null);

  useEffect(() => {
    getFarmListApi()
      .then((r) => setFarms((r.data?.farms || r.data || []).map((f) => ({ value: f.id, label: f.name }))))
      .catch(() => {});
    getCropNamesApi({ lang })
      .then((r) => setCropNames((r.data?.data || r.data || []).map((c) => ({ value: c.common_id ?? c.id, label: c.name || c.crop_name }))))
      .catch(() => {});
  }, [lang]);

  useEffect(() => {
    const params = farmId ? { farm_id: farmId } : {};
    getPlotsApi(params)
      .then((r) => setPlots((r.data?.data || []).map((p) => ({ value: p.id, label: p.name || `Plot ${p.id}` }))))
      .catch(() => {});
  }, [farmId]);

  const fetchCrops = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { years, lang, per_page: perPage, page };
      if (farmId)         params.farm_id   = farmId;
      if (plotIds.length) params.plots     = plotIds;
      if (status !== '')  params.status    = status;
      if (commonId)       params.common_id = commonId;
      const res = await getCropListFilterApi(params);
      const list = res.data?.data || [];
      setCrops(list.sort((a, b) => {
        const aActive = a.status === 1 || a.status === '1' ? 0 : 1;
        const bActive = b.status === 1 || b.status === '1' ? 0 : 1;
        return aActive - bActive;
      }));
      const p = res.data?.pagination;
      if (p) setPagination({ current: p.current_page, total: p.total, pageSize: p.per_page });
    } catch {} finally { setLoading(false); }
  }, [years, farmId, plotIds, status, commonId, perPage, lang]);

  useEffect(() => { sessionStorage.setItem('croplist_years', JSON.stringify(years)); }, [years]);
  useEffect(() => { fetchCrops(1); }, [fetchCrops]);

  const openEdit = async (crop) => {
    const plotsRes = await getPlotsApi({ farm_id: crop.farm_id }).catch(() => ({ data: { data: [] } }));
    const farmPlots = plotsRes.data?.data || [];
    navigate(`/farms/${crop.farm_id}/farm/${crop.id}/edit-crop`, {
      state: { editData: crop, plots: farmPlots },
    });
  };

  const handleDelete = async (crop) => {
    try {
      await deleteCropApi({ crop_id: crop.id });
      message.success('Crop deleted');
      fetchCrops(pagination.current);
    } catch { message.error('Failed to delete crop'); }
  };

  const columns = [
    {
      title: t('crop_name'),
      key: 'crop_name',
      render: (_, record) => (
        <Button
          type="link"
          style={{ padding: 0, color: '#1a6b2a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}
          onClick={() => navigate(`/farms/${record.farm_id}/farm/${record.id}/expenses`, { state: { from: 'crops', crop: { crop_name: record.crop_name || record.crop, verity: record.verity, area: record.crop_area || record.area, area_unit: record.crop_area_unit ?? record.area_unit, image: record.image, plot_name: record.plot_name, farm_name: record.farm_name, crop_date: record.crop_date, harvest_date: record.harvest_date } } })}
        >
          {record.image ? (
            <img src={CROP_NAME_IMAGE(record.image)} alt={record.crop_name || record.crop} style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
          ) : <span>🌱</span>}
          <div style={{ textAlign: 'left' }}>
            <div style={{ textTransform: 'capitalize' }}>{record.crop_name || record.crop}</div>
            {record.verity && <div style={{ fontSize: 11, color: '#888', fontWeight: 400, textTransform: 'capitalize' }}>{record.verity}</div>}
          </div>
        </Button>
      ),
    },
    {
      title: 'Farm / Plot',
      key: 'farm_plot',
      render: (_, r) => (
        <div style={{ fontSize: 13 }}>
          {r.farm_name && <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.farm_name}</div>}
          {r.plot_name && <div style={{ color: '#888', fontSize: 12, textTransform: 'capitalize' }}>📍 {r.plot_name}</div>}
        </div>
      ),
    },
    {
      title: t('area'),
      key: 'area',
      render: (_, r) => {
        const a = r.crop_area || r.area;
        const u = AREA_UNITS[r.crop_area_unit ?? r.area_unit] || r.crop_area_unit || r.area_unit || '';
        return a ? `${a} ${u}` : '—';
      },
    },
    {
      title: t('sowing_date'),
      dataIndex: 'crop_date',
      render: (v, record) => {
        if (!v) return '—';
        const today = dayjs();
        const hd = record.harvest_date ? dayjs(record.harvest_date) : null;
        const endDate = hd && !hd.isAfter(today, 'day') ? hd : today;
        const days = endDate.diff(dayjs(v), 'day');
        return (
          <div>
            <div>{formatDate(v)}</div>
            <div style={{ fontSize: 12, color: '#1a6b2a', marginTop: 2, fontWeight: 500 }}>crop age: {days} days</div>
          </div>
        );
      },
    },
    { title: t('harvest_date'), dataIndex: 'harvest_date', render: formatDate },
    {
      title: t('status'),
      dataIndex: 'status',
      render: (s, record) => {
        const isActive = s === 1 || s === '1';
        const noHarvestDate = !record.harvest_date;
        const harvestDue = record.harvest_date && !dayjs().isBefore(dayjs(record.harvest_date), 'day');
        const showMarkBtn = isActive && (noHarvestDate || harvestDue);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
            <Tag color={isActive ? 'green' : 'orange'} icon={isActive ? <SyncOutlined spin /> : <CheckCircleOutlined />}>
              {isActive ? t('active') : t('harvested')}
            </Tag>
            {showMarkBtn && (
              <Button
                size="small"
                style={{ fontSize: 11, borderColor: '#d46b08', color: '#d46b08', height: 22, padding: '0 6px' }}
                onClick={() => setHarvestingCrop(record)}
              >
                Mark Harvested
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: t('total_expense'),
      dataIndex: 'kharch',
      render: (v) => <span style={{ color: '#fa8c16' }}>{formatCurrency(v)}</span>,
    },
    {
      title: t('total_selling'),
      dataIndex: 'vechan',
      render: (v) => <span style={{ color: '#52c41a' }}>{formatCurrency(v)}</span>,
    },
    {
      title: 'Net Profit / Loss',
      key: 'profit',
      render: (_, r) => {
        const p = (r.vechan || 0) - (r.kharch || 0);
        return <span style={{ color: p >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>{formatCurrency(p)}</span>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
              onClick={() => navigate(`/farms/${record.farm_id}/farm/${record.id}/expenses`, { state: { from: 'crops', crop: { crop_name: record.crop_name || record.crop, verity: record.verity, area: record.crop_area || record.area, area_unit: record.crop_area_unit ?? record.area_unit, image: record.image, plot_name: record.plot_name, farm_name: record.farm_name, crop_date: record.crop_date, harvest_date: record.harvest_date } } })} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete this crop?" onConfirm={() => handleDelete(record)} okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconCrop}) no-repeat center / contain`, mask: `url(${iconCrop}) no-repeat center / contain`, flexShrink: 0 }} />
          {t('crops')}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={() => fetchCrops(1)} loading={loading} />
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Space wrap size={8}>
          <FilterOutlined style={{ color: '#888' }} />
          <Select mode="multiple" value={years} onChange={setYears} options={YEAR_OPTIONS} placeholder="Year" style={{ minWidth: 140 }} />
          <Select allowClear value={farmId} onChange={(v) => { setFarmId(v); setPlotIds([]); }} options={farms} placeholder="All Farms" style={{ minWidth: 150 }} />
          <Select mode="multiple" value={plotIds} onChange={setPlotIds} options={plots} placeholder="All Plots" style={{ minWidth: 150 }} disabled={!farmId} />
          <Select
            allowClear showSearch optionFilterProp="label"
            value={commonId} onChange={setCommonId}
            options={cropNames} placeholder="All Crop Types"
            style={{ minWidth: 160 }}
          />
          <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: 130 }} />
          <Select value={perPage} onChange={setPerPage} options={PER_PAGE_OPTIONS} style={{ width: 110 }} />
        </Space>
      </div>

      {/* Table */}
      <Card style={{ borderRadius: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : crops.length === 0 ? (
          <Empty description="No crops found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={crops}
            columns={columns}
            rowKey="id"
            scroll={{ x: 900 }}
            pagination={{
              current: pagination.current,
              total: pagination.total,
              pageSize: pagination.pageSize,
              onChange: (page) => fetchCrops(page),
              showTotal: (total) => `${total} crops`,
              showSizeChanger: false,
            }}
            onRow={(r) => ({ onDoubleClick: () => navigate(`/farms/${r.farm_id}/farm/${r.id}/expenses`, { state: { from: 'crops', crop: { crop_name: r.crop_name || r.crop, verity: r.verity, area: r.crop_area || r.area, area_unit: r.crop_area_unit ?? r.area_unit, image: r.image, plot_name: r.plot_name, farm_name: r.farm_name } } }) })}
          />
        )}
      </Card>
      <HarvestModal
        open={!!harvestingCrop}
        crop={harvestingCrop}
        onClose={() => setHarvestingCrop(null)}
        onSave={async (payload) => {
          await changeCropStatusApi({
            crop_id: harvestingCrop.id,
            status: payload.status,
            harvest_date: payload.harvest_date,
          });
          message.success('Crop updated');
          setHarvestingCrop(null);
          fetchCrops(pagination.current);
        }}
      />
    </div>
  );
}
